import React, { useEffect, useState, useCallback } from "react";
import { Megaphone, Info, CheckCircle, IndianRupee, ArrowRight, ShieldCheck } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import {
  sendBannerRequestThunk,
  fetchBannerRequestDetail,
} from "../../../features/user/bannerRequestSlice";

import { clearCashPayment } from "../../../features/payment/paymentSlice";
import { createCashPayment, checkCashPaymentStatus } from "../../../features/payment/paymentThunks";
import PaymentOptionModal from "./pyment/PaymentOption";
import PaymentQRModal from "./pyment/PaymentQRModal";
import PaymentSummaryModal from "./pyment/PymentSummary";
import useOnlinePayment from "../../../hooks/useOnlinePayment";

// Custom CSS for better styling (Add this to your CSS file)
/*
.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(0,0,0,0.05);
}
.btn-gradient {
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  color: white;
  border: none;
  transition: transform 0.2s;
}
.btn-gradient:hover { transform: translateY(-2px); color: white; opacity: 0.9; }
*/

const BannerRequest = () => {
  const dispatch = useDispatch();

  const { loading, success, error, requestDetail } = useSelector((state) => state.bannerRequest);
  const enquirySent = Boolean(requestDetail?.enquirySent);
  
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  
  const { startOnlinePayment } = useOnlinePayment();
  const { cashPaymentData, status, payment_status } = useSelector((state) => state.payment);


// useEffect(() => {
//   if (!requestDetail) return;
//   if (requestDetail.status === "completed") return;

//   const interval = setInterval(() => {
//     dispatch(fetchBannerRequestDetail());
//   }, 5000);

//   return () => clearInterval(interval);
// }, [dispatch, requestDetail?.status]);

useEffect(() => {
  if (!requestDetail) return;
  if (requestDetail.status === "approved") return;

  const interval = setInterval(() => {
    dispatch(fetchBannerRequestDetail());
  }, 5000);

  return () => clearInterval(interval);
}, [dispatch, requestDetail?.status]);


  // Function to refresh data without reloading the whole page
  const refreshData = useCallback(() => {
    dispatch(fetchBannerRequestDetail());
  }, [dispatch]);

  /* ---------------- POLLING LOGIC ---------------- */
  useEffect(() => {
    if (!cashPaymentData?.orderId || paymentSuccessful) return;

    const interval = setInterval(() => {
      dispatch(checkCashPaymentStatus(cashPaymentData.orderId));
    }, 3000);

    return () => clearInterval(interval);
  }, [cashPaymentData?.orderId, paymentSuccessful, dispatch]);

  /* ---------------- SUCCESS HANDLER ---------------- */
  useEffect(() => {
    if (payment_status === "paid" && !paymentSuccessful) {
      setPaymentSuccessful(true);
      
      setTimeout(() => {
        setPaymentSuccessful(false);
        dispatch(clearCashPayment());
        refreshData(); // FRESH DATA FETCH INSTEAD OF RELOAD
      }, 3500);
    }
  }, [payment_status, paymentSuccessful, refreshData, dispatch]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

const handleSubmit = async () => {
  const res = await dispatch(sendBannerRequestThunk());

  if (res.meta.requestStatus === "fulfilled") {
    dispatch(fetchBannerRequestDetail()); // 🔥 FORCE UI UPDATE
  }
};

  const handleProceedToPay = () => {
    setSummaryOpen(false);
    setPaymentOpen(true);
  };

  const handlePaymentSelect = async (method) => {
    if (!requestDetail?._id) return;

    if (method === "cash") {
      await dispatch(createCashPayment({
        bookingId: requestDetail._id,
        amount: requestDetail.totalAmount,
        paymentFor: "advt-banner",
      }));
      setPaymentOpen(false);
      return;
    }

    if (method === "online") {
      setPaymentOpen(false);
      startOnlinePayment({
        bookingId: requestDetail._id,
        amount: requestDetail.totalAmount,
        paymentFor: "advt-banner",
        onSuccess: () => {
          refreshData(); // FRESH DATA FETCH INSTEAD OF RELOAD
        },
        onFailure: (err) => console.error(err),
      });
    }
  };

  /* =====================================================
      RENDER HELPER: WRAPPER
  ===================================================== */
  const PageWrapper = ({ children }) => (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center bg-light p-3">
      <div className="card glass-card shadow-lg p-4 p-md-5 text-center" style={{ maxWidth: "500px", width: "100%" }}>
        {children}
      </div>
    </div>
  );


  /* =====================================================
   ✅ COMPLETED STATE (FINAL SUCCESS SCREEN)
===================================================== */
if (requestDetail?.status === "completed") {
  return (
    <PageWrapper>
      <div className="mb-4">
        <div className="bg-success bg-opacity-10 d-inline-block p-4 rounded-circle mb-4">
          <CheckCircle size={64} className="text-success" />
        </div>

        <h2 className="fw-bold">Banner Ad Completed!</h2>
        <p className="text-muted px-md-4">
          Your banner advertisement has been successfully set up and is now live!
        </p>
      </div>

      <button 
        className={`btn btn-lg w-100 py-3 fw-bold rounded-3 shadow-sm ${loading ? 'btn-secondary' : 'btn-primary'}`}
        disabled={loading} 
        onClick={handleSubmit}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm me-2"></span>
        ) : null}
        {loading ? "Processing..." : "Submit My Request"}
      </button>

      <div className="mt-4 bg-light rounded-3 p-3 d-flex align-items-center justify-content-center">
        <Info size={16} className="me-2 text-muted" />
        <span className="small text-muted">
          Your banner is active. You can request a new banner when this one expires.
        </span>
      </div>
    </PageWrapper>
  );
}

/* =====================================================
   💰 PAID STATE (PAYMENT RECEIVED – WAITING FOR SETUP)
===================================================== */
if (
  requestDetail?.paymentStatus === "paid"
) {
  return (
    <PageWrapper>
      <div className="mb-4">
        <div className="bg-primary bg-opacity-10 d-inline-block p-4 rounded-circle mb-4">
          {/* Clock-style feel */}
          <Info size={60} className="text-primary" />
        </div>

        <h2 className="fw-bold">Payment Received</h2>
        <p className="text-muted px-md-4">
          Payment received successfully. Our team is setting up your banner
          advertisement.
        </p>
      </div>

      <div className="bg-light rounded-3 p-3 d-flex align-items-center justify-content-center border">
        <Info size={18} className="me-2 text-muted" />
        <span className="small text-muted">
          Our team is working on setting up your banner.
          This usually takes <strong>1–2 business days</strong>.
        </span>
      </div>
    </PageWrapper>
  );
}


  /* =====================================================
      1️⃣ APPROVED STATE (PAYMENT CARD)
  ===================================================== */
  if (requestDetail?.status === "approved") {
    return (
      <PageWrapper>
        <div className="mb-4">
          <div className="bg-success bg-opacity-10 d-inline-block p-3 rounded-circle mb-3">
            <ShieldCheck size={48} className="text-success" />
          </div>
          <h2 className="fw-bold">Request Approved!</h2>
          <p className="text-muted">Your banner is ready to go live. Just complete the secure payment below.</p>
        </div>

        <div className="bg-light rounded-4 p-4 mb-4 border border-dashed">
          <span className="text-uppercase small fw-bold text-secondary tracking-wider">Total Payable</span>
          <h1 className="fw-black mt-2 mb-0 display-5 d-flex align-items-center justify-content-center">
            <IndianRupee size={32} /> {requestDetail.totalAmount || requestDetail.amount}
          </h1>
        </div>

        <button className="btn btn-lg btn-success w-100 py-3 fw-bold rounded-3 shadow-sm" onClick={() => setSummaryOpen(true)}>
          Proceed to Checkout <ArrowRight size={20} className="ms-2" />
        </button>

        <PaymentSummaryModal
          show={summaryOpen}
          slot={{
            amount: requestDetail?.amount,
            gstPercentage: requestDetail?.gstPercentage,
            gstAmount: requestDetail?.gstAmount,
            totalAmount: requestDetail?.totalAmount,
          }}
          onClose={() => setSummaryOpen(false)}
          onBookClick={handleProceedToPay}
        />
        <PaymentOptionModal open={paymentOpen} onClose={() => setPaymentOpen(false)} onSelect={handlePaymentSelect} />
        <PaymentQRModal
          open={Boolean(cashPaymentData)}
          data={cashPaymentData}
          status={status}
          payment_status={payment_status}
          onClose={() => {
            if (!paymentSuccessful) {
              dispatch(clearCashPayment());
              setPaymentSuccessful(false);
            }
          }}
        />
      </PageWrapper>
    );
  }

  /* =====================================================
      2️⃣ PENDING STATE (ENQUIRY SENT)
  ===================================================== */
  if (enquirySent) {
    return (
      <PageWrapper>
        <div className="mb-4">
          <div className="bg-primary bg-opacity-10 d-inline-block p-4 rounded-circle mb-4">
            <CheckCircle size={60} className="text-primary" />
          </div>
          <h2 className="fw-bold">We've Got It!</h2>
          <p className="text-muted px-md-4">Your banner request has been successfully submitted and is currently being reviewed by our team.</p>
        </div>

        <div className="bg-warning bg-opacity-10 p-3 rounded-3 d-flex align-items-center justify-content-center border border-warning border-opacity-25">
          <Info size={18} className="text-warning me-2" />
          <span className="small fw-medium">Estimated approval: 2–3 business days.</span>
        </div>
      </PageWrapper>
    );
  }

  /* =====================================================
      3️⃣ INITIAL STATE (REQUEST FORM)
  ===================================================== */
  return (
    <PageWrapper>
      <div className="mb-4">
        <div className="bg-primary bg-opacity-10 d-inline-block p-4 rounded-circle mb-4">
          <Megaphone size={50} className="text-primary" />
        </div>
        <h2 className="fw-bold">Grow Your Business</h2>
        <p className="text-muted">Reach thousands of customers by placing your banner advertisement on our platform.</p>
      </div>
      <button 
        className={`btn btn-lg w-100 py-3 fw-bold rounded-3 shadow-sm ${loading ? 'btn-secondary' : 'btn-primary'}`}
        disabled={loading} 
        onClick={handleSubmit}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm me-2"></span>
        ) : null}
        {loading ? "Processing..." : "Submit My Request"}
      </button>

      {error && (
        <div className="alert alert-danger mt-3 py-2 small border-0">
          {error.message || "Something went wrong. Please try again."}
        </div>
      )}

      <div className="mt-4 pt-3 border-top">
        <div className="d-flex align-items-center justify-content-center text-muted">
          <Info size={16} className="me-2" />
          <span className="small">Our team will contact you shortly after submission.</span>
        </div>
      </div>
      <style>{`
      /* Container Background - Soft gradient for depth */
.bg-light {
  background: radial-gradient(circle at top right, #f8f9ff 0%, #e2e8f0 100%) !important;
}

/* Glassmorphism Card Style */
.glass-card {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 24px !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05) !important;
}

/* Icon Backgrounds */
.bg-primary.bg-opacity-10 {
  background-color: rgba(99, 102, 241, 0.1) !important;
}

.text-primary {
  color: #6366f1 !important;
}

/* Custom Buttons */
.btn-primary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
  border: none !important;
  transition: all 0.3s ease !important;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(99, 102, 241, 0.3) !important;
}

.btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  transition: all 0.3s ease !important;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(16, 185, 129, 0.3) !important;
}

/* Payment Section Styling */
.fw-black {
  font-weight: 900;
  letter-spacing: -1px;
}

.tracking-wider {
  letter-spacing: 0.1em;
}

.border-dashed {
  border-style: dashed !important;
  border-width: 2px !important;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .glass-card {
    padding: 2rem 1.5rem !important;
    margin: 10px;
    border-radius: 20px !important;
  }
}
      `}</style>
    </PageWrapper>
  );
};

export default BannerRequest;